import type { TicketEarningsEvent } from '@pulsify/shared/dto'

const earningsFilterValues = ['upcoming', 'past', 'all'] as const
const earningsSortValues = ['date', 'gross', 'sold'] as const
const sortDirectionValues = ['asc', 'desc'] as const

export type EarningsFilter = (typeof earningsFilterValues)[number]
export type EarningsSort = (typeof earningsSortValues)[number]
type SortDirection = (typeof sortDirectionValues)[number]

interface EarningsView {
  filter: EarningsFilter
  sort: EarningsSort
  direction: SortDirection
}

export interface EarningsSearch {
  filter?: EarningsFilter
  sort?: EarningsSort
  dir?: SortDirection
}

const pick = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  values.find((known) => known === value)

function defaultDirection(sort: EarningsSort, filter: EarningsFilter): SortDirection {
  return sort === 'date' && filter === 'upcoming' ? 'asc' : 'desc'
}

export function parseEarningsSearch(search: Record<string, unknown>): EarningsSearch {
  const filter = pick(earningsFilterValues, search.filter)
  const sort = pick(earningsSortValues, search.sort)
  const dir = pick(sortDirectionValues, search.dir)
  return {
    ...(filter && filter !== 'all' ? { filter } : {}),
    ...(sort && sort !== 'date' ? { sort } : {}),
    ...(dir ? { dir } : {}),
  }
}

export function earningsView(search: EarningsSearch): EarningsView {
  const filter = search.filter ?? 'all'
  const sort = search.sort ?? 'date'
  return { filter, sort, direction: search.dir ?? defaultDirection(sort, filter) }
}

export function earningsSearch(view: EarningsView): EarningsSearch {
  return {
    filter: view.filter === 'all' ? undefined : view.filter,
    sort: view.sort === 'date' ? undefined : view.sort,
    dir: view.direction === defaultDirection(view.sort, view.filter) ? undefined : view.direction,
  }
}

export function sortBy(view: EarningsView, sort: EarningsSort): EarningsView {
  if (view.sort === sort) {
    return { ...view, direction: view.direction === 'asc' ? 'desc' : 'asc' }
  }
  return { ...view, sort, direction: defaultDirection(sort, view.filter) }
}

export function filterBy(view: EarningsView, filter: EarningsFilter): EarningsView {
  return { ...view, filter, direction: defaultDirection(view.sort, filter) }
}

function sortValues(row: TicketEarningsEvent, sort: EarningsSort): [number, number] {
  if (sort === 'gross') return [row.grossCents, row.netCents]
  if (sort === 'sold') return [row.ticketsSold, row.freeTickets]
  return [new Date(row.startTime).getTime(), 0]
}

function compare(a: TicketEarningsEvent, b: TicketEarningsEvent, view: EarningsView): number {
  const sign = view.direction === 'asc' ? 1 : -1
  const [a1, a2] = sortValues(a, view.sort)
  const [b1, b2] = sortValues(b, view.sort)
  return (
    sign * (a1 - b1 || a2 - b2) ||
    new Date(b.startTime).getTime() - new Date(a.startTime).getTime() ||
    a.title.localeCompare(b.title)
  )
}

export function visibleEarnings(
  rows: readonly TicketEarningsEvent[],
  view: EarningsView,
  now: Date,
): TicketEarningsEvent[] {
  return rows
    .filter((row) => {
      if (view.filter === 'all') return true
      const over = new Date(row.endTime) < now
      return view.filter === 'past' ? over : !over
    })
    .sort((a, b) => compare(a, b, view))
}

export function eventDateLabel(iso: string, locale: string, now: Date): string {
  const date = new Date(iso)
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'Europe/Vienna',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}
